import axios from 'axios';
import React, { useState } from 'react';
import { Form, Icon, Input, Modal, Button } from 'semantic-ui-react';
import ContactIcon from '../../../resources/call.png';
import MailIcon from '../../../resources/email-icon.png';
import MapIcon from '../../../resources/map-icon.png';
import './sectionOne.scss';

import { CONFIG } from '../../../config'

// Fields required for submission
const REQUIRED_FIELDS = ['firstName', 'company', 'email', 'subject', 'message'];

// Full initial state — includes optional fields (lastName, role, industry) too,
// so their inputs stay controlled from the start.
const EMPTY_FORM = {
	firstName: '',
	lastName: '',
	role: '',
	company: '',
	industry: '',
	email: '',
	subject: '',
	message: '',
};

const FIELD_LABELS = {
	firstName: 'First name',
	lastName: 'Last name',
	role: 'Role',
	company: 'Company',
	industry: 'Industry',
	email: 'Email Address',
	subject: 'Subject',
	message: 'Message',
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SectionOne = () => {
	const [data, setData] = useState(EMPTY_FORM);
	const [errors, setErrors] = useState({});
	const [submitting, setSubmitting] = useState(false);
	const [modal, setModal] = useState({ open: false, success: false, message: '' });

	const handleChange = (e, { name, value }) => {
		setData((prevData) => ({
			...prevData,
			[name]: value,
		}));
		if (errors[name]) {
			setErrors((prevErrors) => ({
				...prevErrors,
				[name]: undefined,
			}));
		}
	};

	const validate = () => {
		const newErrors = {};

		REQUIRED_FIELDS.forEach((field) => {
			if (!data[field] || !data[field].trim()) {
				newErrors[field] = `${FIELD_LABELS[field]} is required`;
			}
		});

		if (data.email && data.email.trim() && !EMAIL_REGEX.test(data.email.trim())) {
			newErrors.email = 'Please enter a valid email address';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const closeModal = () => setModal({ open: false, success: false, message: '' });

	const handleSubmit = () => {
		// Ignore extra clicks while a submission is already in flight
		if (submitting) {
			return;
		}

		if (!validate()) {
			setModal({
				open: true,
				success: false,
				message: 'Please fill in all required fields correctly before submitting.',
			});
			return;
		}

		setSubmitting(true);

		axios
			.post(`${CONFIG.api_base_url}eaciit-website/contact`, data)
			.then((response) => {
				if (response.status === 200) {
					setData(EMPTY_FORM);
					setErrors({});
					setModal({
						open: true,
						success: true,
						message: 'Your message has been submitted successfully. We will get back to you soon.',
					});
				} else {
					setModal({
						open: true,
						success: false,
						message: 'Something went wrong. Please try again.',
					});
				}
			})
			.catch((error) => {
				console.log(error.response);
				setModal({
					open: true,
					success: false,
					message: 'Something went wrong. Please try again.',
				});
			})
			.finally(() => setSubmitting(false));
	};

	const redirectToLinkedIn = (name) => {
		let url = '';
		switch (name) {
			case 'navdeep':
				url = 'https://www.linkedin.com/in/navdeep-jain-98915a20/';
				break;
			case 'eaciit':
				url = 'https://www.linkedin.com/company/eaciit/';
				break;
			case 'usa':
				url = 'https://www.linkedin.com/in/namita-anand-025ab18/';
				break;
			default:
				break;
		}
		window.open(url, '_blank').focus();
	};

	const {
		firstName,
		lastName,
		role,
		company,
		industry,
		email,
		subject,
		message,
	} = data;

	return (
		<div className='ContactUsSectionOne'>
			<div className='ContactUsSectionOne-content'>
				<div className='ContactUsSectionOne-left'>
					<h1 className='title'>Contact Us</h1>
					<div className='concact-form'>
						<Form onSubmit={handleSubmit}>
							<Form.Group widths='equal'>
								<Form.Input
									fluid
									required
									name='firstName'
									label='First name'
									size='large'
									placeholder='First name'
									value={firstName}
									onChange={handleChange}
									error={
										errors.firstName
											? { content: errors.firstName, pointing: 'above' }
											: false
									}
								/>
								<Form.Input
									fluid
									name='lastName'
									label='Last name'
									size='large'
									placeholder='Last name'
									value={lastName}
									onChange={handleChange}
									error={
										errors.lastName
											? { content: errors.lastName, pointing: 'above' }
											: false
									}
								/>
							</Form.Group>
							<Form.Field
								size='large'
								name='role'
								placeholder='Role'
								id='form-input-control-error-role'
								control={Input}
								label='Role'
								className='form-input'
								value={role}
								onChange={handleChange}
								error={
									errors.role
										? { content: errors.role, pointing: 'above' }
										: false
								}
							/>
							<Form.Field
								required
								size='large'
								name='company'
								placeholder='Company'
								id='form-input-control-error-company'
								control={Input}
								label='Company'
								className='form-input'
								value={company}
								onChange={handleChange}
								error={
									errors.company
										? { content: errors.company, pointing: 'above' }
										: false
								}
							/>
							<Form.Field
								size='large'
								name='industry'
								placeholder='Industry'
								id='form-input-control-error-industry'
								control={Input}
								label='Industry'
								className='form-input'
								value={industry}
								onChange={handleChange}
								error={
									errors.industry
										? { content: errors.industry, pointing: 'above' }
										: false
								}
							/>
							<Form.Field
								required
								size='large'
								name='email'
								placeholder='Email Address'
								id='form-input-control-error-email'
								control={Input}
								label='Email Address'
								className='form-input'
								value={email}
								onChange={handleChange}
								error={
									errors.email
										? { content: errors.email, pointing: 'above' }
										: false
								}
							/>
							<Form.Field
								required
								size='large'
								name='subject'
								placeholder='Subject'
								id='form-input-control-error-subject'
								control={Input}
								label='Subject'
								className='form-input'
								value={subject}
								onChange={handleChange}
								error={
									errors.subject
										? { content: errors.subject, pointing: 'above' }
										: false
								}
							/>
							<Form.TextArea
								required
								label='Your Message'
								name='message'
								className='form-input'
								size='large'
								placeholder='Your Message'
								rows='6'
								value={message}
								onChange={handleChange}
								error={
									errors.message
										? { content: errors.message, pointing: 'above' }
										: false
								}
							/>
							<Form.Button
								className={`submit-button ${submitting ? 'submit-button--disabled' : ''}`}
								disabled={submitting}
							>
								{submitting ? (
									<>
										<Icon name='spinner' loading /> Submitting...
									</>
								) : (
									'Submit Form'
								)}
							</Form.Button>
						</Form>
					</div>
				</div>
				<div className='ContactUsSectionOne-right'>
					<div className='contact-information'>
						<div className='head-office'>
							<div className='header'>
								<img src={MapIcon} className='contactUs-map-icon' alt='map-icon'/>
								<span className='title-header'>Head Office Singapore</span>
							</div>
							<div className='header-detail'>
								EACIIT Vyasa PTE. LTD.
								<br />
								36 Robinson Road,
								<br />
								#20-01 City House,
								<br />
								Singapore - 068877
							</div>
							<div
								className='connect-with-us'
								onClick={() => redirectToLinkedIn('eaciit')}
							>
								<Icon name='linkedin square' /> <span>Connect with us!</span>
							</div>
						</div>
						<div className='head-office' style={{ marginTop: '10px' }}>
							<div className='header'>
								<img src={MailIcon} className='contactUs-mail-icon' alt='mail-icon'/>
								<span className='title-header title-header-email' style={{ marginLeft: '-5px' }}>
									enquiries@eaciit.com
								</span>
							</div>
						</div>
						<div className='head-office' style={{ marginTop: '10px' }}>
							<div className='header'>
								<img src={ContactIcon} className='contactUs-call-icon' alt='call-icon'/>
								<span className='title-header title-header-call'>Primary Contact</span>
							</div>
							<div className='header-detail'>
								Navdeep Jain
								<br />
								navdeep@eaciit.com
								<br />
								Tel: +6592329682
								<br />
							</div>
							<div
								className='connect-with-us'
								onClick={() => redirectToLinkedIn('navdeep')}
							>
								<Icon name='linkedin square' />{' '}
								<span>Connect with Navdeep</span>
							</div>
						</div>
					</div>
					<hr className='divider' />
					<div className='contact-information'>
						<div className='regional-office'>
							<div className='header'>
								<span className='title-header'>Regional Offices</span>
							</div>
							<div className='header-detail'>
								<span style={{ color: '#EC6927' }}>Delhi – India</span>
								<br />
								Primary Contact
								<br />
								Namita Anand
								<br />
								namita@eaciit.com
								<br />
								Tel: +919953160132
								<br />
							</div>
							<div
								className='connect-with-us'
								onClick={() => redirectToLinkedIn('usa')}
							>
								<Icon name='linkedin square' />
							</div>
						</div>
					</div>
				</div>
			</div>

			<Modal
				open={modal.open}
				onClose={closeModal}
				size='mini'
				className='contact-form-modal'
			>
				<Modal.Header>
					{modal.success ? 'Thank you!' : 'Error'}
				</Modal.Header>
				<Modal.Content>
					<p>{modal.message}</p>
				</Modal.Content>
				<Modal.Actions>
					<Button primary onClick={closeModal}>
						OK
					</Button>
				</Modal.Actions>
			</Modal>
		</div>
	);
};

export default SectionOne;
